import { Alert, AlertTitle, Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { describeApiError } from '@/lib/api/errors';
import { DATA_QUALITY_LABEL, type DataQuality } from '@/lib/dataQuality';

export function LoadingState({ label = 'Wczytywanie danych…' }: { label?: string }) {
  return (
    <Stack spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', py: 6 }} role="status" aria-live="polite">
      <CircularProgress size={28} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

export function ErrorState({ error, context }: { error: unknown; context?: string }) {
  return (
    <Alert severity="error" role="alert" sx={{ my: 2 }}>
      <AlertTitle>Nie udało się pobrać danych</AlertTitle>
      <Typography variant="body2">{describeApiError(error)}</Typography>
      {context ? (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {context}
        </Typography>
      ) : null}
    </Alert>
  );
}

export function EmptyState({
  title,
  description,
  kartaId,
}: {
  title: string;
  description?: string;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15).
   *
   * Dlaczego atrybut, a nie dopasowanie po etykiecie: sprawdzenie 100 etykiet z inwentarza
   * pokazało, że tylko 66 rozstrzyga się do jednego miejsca. Tutaj jest gorzej niż średnio —
   * cały widoczny tekst przychodzi z zewnątrz w `title`/`description`, więc komponent nie ma
   * ŻADNEGO własnego napisu, po którym dałoby się go zaadresować. Dwa stany puste Transakcji
   * („Brak transakcji" i „Nie znaleziono płatności o numerze …") to ta sama karta inwentarza
   * w dwóch brzmieniach, a jedno z nich zawiera wpisaną przez operatora liczbę.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  return (
    <Paper variant="outlined" data-cc-card-id={kartaId} sx={{ p: 4, textAlign: 'center', my: 2 }}>
      {/*
        `component="h4"` przy zachowanym wariancie `subtitle1`.

        MUI mapuje `subtitle1` na znacznik <h6>, a tytuł ekranu jest <h4> — powstawał więc
        przeskok o dwa poziomy. Czytnik ekranu buduje z nagłówków spis treści strony i taki
        przeskok czyta jako „brakuje sekcji pośredniej": użytkownik nawigujący po nagłówkach
        szuka poziomu, którego nie ma.

        Rozdzielenie WYGLĄDU od ZNACZENIA jest tu właściwym narzędziem — zmiana wariantu na `h5`
        naprawiłaby semantykę, ale zmieniła krój i stopień pisma we wszystkich stanach pustych.
      */}
      <Typography variant="subtitle1" component="h4">
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {description}
        </Typography>
      ) : null}
    </Paper>
  );
}

/**
 * Pusta lista sygnałów ma DWIE zupełnie różne przyczyny i mylenie ich jest kosztowne:
 *
 *   • przeanalizowano N pozycji, żadna nie przekroczyła progów  →  to jest WYNIK.
 *     Operator może na tej podstawie spokojnie nie robić nic.
 *   • nie było czego analizować (zero pozycji w oknie)          →  to NIE jest wynik.
 *     Operator, który przeczyta tu „nic istotnego się nie dzieje”, zostanie wprowadzony w błąd —
 *     bo prawda brzmi „nie wiemy, czy się dzieje”.
 *
 * Ten komponent istnieje po to, żeby ta różnica nie zależała od tego, czy ktoś pamiętał
 * o niej na konkretnym ekranie.
 */
export function InsightOutcome({
  analysed,
  window,
  subject = 'pozycji',
  kartaId,
  powodBrakuAnalizy,
}: {
  /**
   * Ile pozycji silnik REALNIE wziął pod uwagę — nie ile znalazł sygnałów i nie ile było
   * kandydatek.
   *
   * Ta różnica kosztowała już jeden fałszywy komunikat na produkcji: ekran Powiązań podawał tu
   * liczbę szkół dopasowanych w złączeniu (15), podczas gdy silnik zbieżności nie przeanalizował
   * ani jednej — wszystkie odpadły wcześniej na braku porównania punktualności. Ekran napisał
   * „przeanalizował 15 szkół i żadna nie przekroczyła progów. To jest wynik", i było to nieprawdą.
   */
  analysed: number;
  window?: { date_from: string; date_to: string };
  /** Dopełniacz liczby mnogiej, np. „dań”, „szkół”. */
  subject?: string;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15).
   *
   * Dlaczego atrybut, a nie dopasowanie po etykiecie: sprawdzenie 100 etykiet z inwentarza
   * pokazało, że tylko 66 rozstrzyga się do jednego miejsca. Ten komponent stoi na czterech
   * ekranach (Dziś, Insighty, Dania, Powiązania) i na każdym mówi to samo — a jego tytuł
   * dodatkowo zależy od tego, KTÓRA z dwóch gałęzi się wyrenderowała.
   *
   * Identyfikator opisuje KARTĘ, nie gałąź: „brak danych do analizy" i „brak istotnych
   * sygnałów" to dwie odpowiedzi tego samego miejsca na ekranie, więc noszą ten sam adres.
   * Walidacja czyta treść pod adresem — rozróżnienie stanów zostaje w treści, tam gdzie było.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
  /**
   * Dlaczego nie było czego analizować — gdy silnik odrzucił kandydatki PRZED porównaniem.
   *
   * Bez tego zdania „brak danych do analizy" brzmi jak puste źródło, a bywa czymś zupełnie
   * innym: dane są, tylko brakuje JEDNEGO wymiaru potrzebnego do zestawienia. To dwie różne
   * naprawy i dwie różne rozmowy z właścicielem danych.
   */
  powodBrakuAnalizy?: string | null;
}) {
  const okno = window ? ` (${window.date_from} → ${window.date_to})` : '';

  if (analysed === 0) {
    return (
      <Alert severity="warning" icon={false} data-cc-card-id={kartaId}>
        <AlertTitle>Nie było czego przeanalizować{okno}</AlertTitle>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
          <DataQualityBadge quality="MISSING_HISTORY" reason={powodBrakuAnalizy ?? 'W wybranym oknie nie ma żadnej obserwacji.'} />
        </Stack>
        <Typography variant="body2">
          Silnik nie porównał ani jednej {subject.replace(/y$/, 'i')}. To NIE znaczy, że nic się nie dzieje —
          to znaczy, że nie wiemy.
        </Typography>
        {powodBrakuAnalizy ? (
          <Typography variant="body2" sx={{ mt: 1, color: 'var(--tekst-2)' }}>
            {powodBrakuAnalizy}
          </Typography>
        ) : null}
      </Alert>
    );
  }

  return (
    <Alert severity="info" data-cc-card-id={kartaId}>
      <AlertTitle>Brak istotnych sygnałów{okno}</AlertTitle>
      <Typography variant="body2">
        Silnik przeanalizował {analysed} {subject} i żadna nie przekroczyła progów istotności. To jest wynik —
        nie wypełniamy ekranu słabymi sygnałami.
      </Typography>
    </Alert>
  );
}

const QUALITY_COLOR: Record<DataQuality, 'success' | 'warning' | 'error' | 'default'> = {
  GOOD: 'success',
  PARTIAL: 'warning',
  LOW_SAMPLE: 'warning',
  MISSING_HISTORY: 'error',
  UNRELIABLE_JOIN: 'error',
  UNKNOWN: 'default',
};

export function DataQualityBadge({
  quality,
  reason,
  kartaId,
}: {
  quality: DataQuality;
  reason?: string;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15).
   *
   * Dlaczego atrybut, a nie dopasowanie po etykiecie: sprawdzenie 100 etykiet z inwentarza
   * pokazało, że tylko 66 rozstrzyga się do jednego miejsca. Odznaka jakości jest skrajnym
   * przypadkiem tego problemu — jej napis pochodzi z sześcioelementowego słownika
   * (`DATA_QUALITY_LABEL`), więc ten sam tekst powtarza się w każdym wierszu rankingu szkół
   * i w każdej karcie metryki na ekranie.
   *
   * UWAGA PRZY WYWOŁANIU: `MetricCard` i inne karty renderują odznakę WEWNĄTRZ siebie i nie
   * przekazują jej swojego `kartaId` — inaczej jeden adres wskazywałby dwa elementy DOM.
   * Prop podaje się tylko tam, gdzie sama odznaka jest kartą inwentarza.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={QUALITY_COLOR[quality]}
      label={DATA_QUALITY_LABEL[quality]}
      title={reason}
      data-cc-card-id={kartaId}
    />
  );
}

/**
 * Ekran celowo pusty. Nie pokazuje żadnych liczb — bo ich nie ma.
 * To jest świadomy wybór: pusty ekran z powodem jest uczciwy, atrapa z ładnym wykresem nie jest.
 *
 * BEZ `kartaId` (R5-15) — i to nie jest przeoczenie. Inwentarz kart obejmuje jedenaście ekranów
 * ŻYWYCH; ten komponent renderuje się wyłącznie z `Placeholder` w `app/router.tsx`, czyli na
 * ekranach o dojrzałości `planned`/`blocked`, których w inwentarzu nie ma. Nie niesie też cudzej
 * karty: `children` jest w kodzie nieużywane — żadne wywołanie nie podaje dzieci. Adres nadaje
 * się miejscu, w którym stoi liczba do sprawdzenia; tutaj takiego miejsca nie ma. Gdy któryś
 * z tych ekranów dostanie dane, dostanie też własny komponent — i wtedy własny identyfikator.
 */
export function NotBuiltYet({
  title,
  maturity,
  note,
  children,
}: {
  title: string;
  maturity: 'planned' | 'blocked';
  note?: string;
  children?: ReactNode;
}) {
  const blocked = maturity === 'blocked';
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{title}</Typography>
        <Chip
          size="small"
          color={blocked ? 'error' : 'default'}
          variant="outlined"
          label={blocked ? 'ZABLOKOWANE' : 'ZAPLANOWANE'}
        />
      </Stack>
      <Alert severity={blocked ? 'warning' : 'info'}>
        <AlertTitle>{blocked ? 'Ten ekran jest zablokowany danymi' : 'Ten ekran jeszcze nie powstał'}</AlertTitle>
        <Typography variant="body2">
          {note ??
            'Zakres R5-15-001 obejmował fundament, nie komplet ekranów. Nic tu nie jest ukryte — po prostu nie ma jeszcze czego pokazać.'}
        </Typography>
      </Alert>
      {children}
    </Box>
  );
}
